import React, { useState, useEffect } from "react";
import { GoPlus } from "react-icons/go";
import { FiEdit2, FiX, FiCheck } from "react-icons/fi";
import styles from "./Taxonomy.module.css";
import {
  getTaxonomyByType,
  getLanguage,
  updateTaxonomyValue,
  deleteTaxonomyValue,
  GenresList,
  GenresCreate,
  updateGenres,
  GenresDelete,
  ratingList,
  ratingCreate,
  ratingUpdate,
  ratingDelete,
} from "../../api/HttpApis";
import { useRbac } from "../../rbac/RbacContext";
import { showToast } from "../../utils/Toastify/Toastify";

// Every backend behind this shared component returns errors in the same
// { status, message } envelope (see apiResponse.js) — one extractor works
// for Genre, Rating, and the generic GlobalKeyValue types alike.
const apiErrorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback;

const TaxonomySection = ({ type, icon, title }) => {
  const { can } = useRbac();
  const canCreateTaxonomy = can("categories", "create");
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchItems = async () => {
      try {
        setIsLoading(true);
        const res =
          type === "GENRES"
            ? await GenresList()
            : type === "CONTENT_RATING"
              ? await ratingList()
              : await getTaxonomyByType(type);

        const raw = res?.data?.data || res?.data || [];
        const mapped = raw.map((it) => ({
          id: it.id ?? it._id,
          key: it.key,
          sort_order: it.sort_order,
          status: it.status,
          name: it.value ?? it.name ?? it.key,
        }));

        if (isMounted) setItems(mapped);
      } catch (error) {
        console.error(`Failed to fetch ${type} taxonomy:`, error);
        if (isMounted) {
          setItems([]);
          showToast(apiErrorMessage(error, `Failed to load ${title}`), "error");
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchItems();

    return () => {
      isMounted = false;
    };
  }, [type]);

  const handleAdd = async () => {
    const trimmed = newLabel.trim();
    if (!trimmed) return;

    try {
      const res =
        type === "GENRES"
          ? await GenresCreate({ name: trimmed })
          : type === "CONTENT_RATING"
            ? await ratingCreate({ name: trimmed, sort_order: items.length + 1 })
            : await getLanguage(type, trimmed, trimmed);

      const createdItem = res?.data?.data ?? res?.data;
      const newItem = {
        id: createdItem?.id ?? createdItem?._id ?? Date.now(),
        key: createdItem?.key ?? trimmed,
        sort_order: createdItem?.sort_order ?? items.length + 1,
        status: createdItem?.status,
        name: createdItem?.name ?? createdItem?.value ?? trimmed,
      };

      setItems((prev) => [...prev, newItem]);
      setNewLabel("");
      setIsAdding(false);
      showToast(`"${trimmed}" added`, "success");
    } catch (error) {
      console.error(`Failed to add ${type} item:`, error);
      showToast(apiErrorMessage(error, `Failed to add "${trimmed}"`), "error");
    }
  };

  const handleCancelAdd = () => {
    setNewLabel("");
    setIsAdding(false);
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditValue(item.name);
  };

  const saveEdit = async () => {
    const trimmed = editValue.trim();
    if (!trimmed) return;

    const editingItem = items.find((it) => it.id === editingId);
    if (!editingItem) return;

    try {
      if (type === "GENRES") {
        await updateGenres(editingId, { name: trimmed });
      } else if (type === "CONTENT_RATING") {
        await ratingUpdate(editingId, {
          name: trimmed,
          sort_order:
            editingItem.sort_order ??
            items.findIndex((it) => it.id === editingId) + 1,
          status: editingItem.status ?? 1,
        });
      } else {
        await updateTaxonomyValue(editingId, {
          type,
          key: editingItem.key ?? trimmed,
          value: trimmed,
        });
      }

      setItems((prev) =>
        prev.map((it) => (it.id === editingId ? { ...it, name: trimmed } : it))
      );
      setEditingId(null);
      setEditValue("");
      showToast(`"${trimmed}" updated`, "success");
    } catch (error) {
      console.error(`Failed to update ${type} item:`, error);
      showToast(apiErrorMessage(error, "Failed to update item"), "error");
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const requestDelete = (item) => {
    setConfirmDeleteItem(item);
  };
  const confirmDelete = async () => {
    if (!confirmDeleteItem) return;
    const id = confirmDeleteItem.id;

    try {
      setIsDeleting(true);
      if (type === "GENRES") {
        await GenresDelete(id);
      } else if (type === "CONTENT_RATING") {
        await ratingDelete(id);
      } else {
        await deleteTaxonomyValue(id);
      }
      setItems((prev) => prev.filter((it) => it.id !== id));
      showToast(`"${confirmDeleteItem.name}" deleted`, "success");
    } catch (error) {
      console.error(`Failed to delete ${type} item:`, error);
      showToast(
        apiErrorMessage(error, `Failed to delete "${confirmDeleteItem.name}"`),
        "error"
      );
    } finally {
      setIsDeleting(false);
      setConfirmDeleteItem(null);
    }
  };
  const cancelDelete = () => {
    setConfirmDeleteItem(null);
  };

  const handleClearAll = () => {
    setItems([]);
  };

  return (
    <div className={styles.sectionContainer}>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.titleGroup}>
            <span className={styles.icon} role="img" aria-label={type}>
              {icon}
            </span>
            <h3 className={styles.title}>{title}</h3>
          </div>
          <div className={styles.headerActions}>
            {canCreateTaxonomy && (
              <button
                className={styles.ghostButton}
                type="button"
                onClick={() => setIsAdding(true)}
              >
                <GoPlus /> Add Item
              </button>
            )}
          </div>
        </div>

        <div className={styles.tagsRow}>
          {isLoading && (
            <span className={styles.emptyText}>Loading...</span>
          )}

          {!isLoading && items.length === 0 && !isAdding && (
            <span className={styles.emptyText}>No items yet. Add your first one</span>
          )}

          {!isLoading && items.map((item) =>
            editingId === item.id ? (
              <div key={item.id} className={styles.editTag}>
                <input
                  className={styles.editInput}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  autoFocus
                />
                <button
                  className={styles.iconBtn}
                  type="button"
                  onClick={saveEdit}
                  aria-label="save"
                >
                  <FiCheck />
                </button>
                <button
                  className={styles.iconBtn}
                  type="button"
                  onClick={cancelEdit}
                  aria-label="cancel"
                >
                  <FiX />
                </button>
              </div>
            ) : (
              <div key={item.id} className={styles.tag}>
                <span>{item.name}</span>
                <button
                  className={styles.iconBtn}
                  type="button"
                  onClick={() => startEdit(item)}
                  aria-label="edit"
                >
                  <FiEdit2 />
                </button>
                <button
                  className={styles.iconBtn}
                  type="button"
                  onClick={() => requestDelete(item)}
                  aria-label="remove"
                >
                  <FiX />
                </button>
              </div>
            )
          )}
        </div>

        {isAdding && (
          <div className={styles.addRow}>
            <input
              className={styles.addInput}
              placeholder="Enter Label"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              autoFocus
            />
            <button className={styles.addBtn} type="button" onClick={handleAdd}>
              Add
            </button>
            <button
              className={styles.cancelBtn}
              type="button"
              onClick={handleCancelAdd}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* NEW: Confirmation dialog, only rendered when a delete is pending */}
      {confirmDeleteItem && (
        <div className={styles.overlay}>
          <div className={styles.confirmBox}>
            <h3 className={styles.confirmTitle}>Delete item</h3>
            <p className={styles.confirmMessage}>
              Are you sure you want to delete{" "}
              <strong>{confirmDeleteItem.name}</strong>?
            </p>
            <div className={styles.confirmActions}>
              <button
                className={styles.deleteBtn}
                type="button"
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes"}
              </button>
              <button
                className={styles.cancelBtn}
                type="button"
                onClick={cancelDelete}
                disabled={isDeleting}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxonomySection;